import {
  CreateEventRequest,
  CreateSessionEventRequest,
  DeleteEventsRequest,
  EventRecord,
  isSessionEventId,
  normalizeEventRecord,
  UpdateEventRequest,
} from "@/shared/domain/events/EventRecord.ts";
import { getPaginatedTagsForOrg } from "@/shared/domain/getPaginatedTagsForOrg";
import {
  HttpClient,
  PaginatedResponse,
  robotoHeaders,
  RobotoUnauthorizedException,
} from "@/shared/http";
import { bigintReplacer, parseTimestampsAsBigInt } from "@/shared/json";
import { capitalizeFirst } from "@/shared/text/capitalizeFirst";

/**
 * The most session event listings `getWorkspaceEvents` has in flight at once.
 * A workspace usually holds a handful of sessions, so this only bounds the
 * burst from an unusually large one.
 */
export const MAX_CONCURRENT_SESSION_EVENT_LISTINGS = 20;

interface Options {
  abortSignal: AbortSignal;
  resourceOwnerId: string;
  searchParams: URLSearchParams;
}

/**
 * Options for the org-wide event vocabularies (tags, metadata keys, names).
 * `includeSessionEvents` adds the session events' values to the dataset
 * events'; set it in an org that has session events.
 */
type VocabularyOptions = Partial<Options> & { includeSessionEvents?: boolean };

/**
 * `request`'s result, or `empty` when it fails with
 * `RobotoUnauthorizedException`. The `/v2/events` vocabulary routes refuse a
 * caller who may not view the org's sessions that way, and such a caller sees
 * dataset events' values only.
 */
function emptyIfUnauthorized<T>(request: Promise<T>, empty: T): Promise<T> {
  return request.catch((error: unknown) => {
    if (error instanceof RobotoUnauthorizedException) {
      return empty;
    }
    throw error;
  });
}

const unique = (values: string[]): string[] => [...new Set(values)];

/** The most event IDs one call to a delete route takes, as the server enforces. */
const MAX_EVENTS_PER_DELETE_BATCH = 1000;

/** `ids` in consecutive batches of at most {@link MAX_EVENTS_PER_DELETE_BATCH}. */
function chunk(ids: string[]): string[][] {
  const batches: string[][] = [];
  for (
    let start = 0;
    start < ids.length;
    start += MAX_EVENTS_PER_DELETE_BATCH
  ) {
    batches.push(ids.slice(start, start + MAX_EVENTS_PER_DELETE_BATCH));
  }
  return batches;
}

interface WorkspaceEventsRequestBody {
  dataset_ids: string[];
  file_ids: string[];
  page_token?: string;
}

/**
 * The resource path of one event: under `/v2/events` for a session event and
 * `/v1/events` for a dataset event, chosen by the ID's prefix.
 */
function eventPath(eventId: string): string {
  return isSessionEventId(eventId)
    ? `v2/events/id/${eventId}`
    : `v1/events/id/${eventId}`;
}

// Converts a URL fragment like "foo/bar" to a string like "FooBar"
function toPascalCase(urlFragment: string): string {
  return urlFragment.split("/").map(capitalizeFirst).join("");
}

export class EventService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * Create an event on a dataset's data (`CreateEventRequest`, sent to
   * `/v1/events/create`) or on a session (`CreateSessionEventRequest`, sent to
   * `/v2/events`).
   */
  public async createEvent(
    orgId: string,
    event: CreateEventRequest | CreateSessionEventRequest,
    options?: Partial<Options>,
  ): Promise<EventRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      "session_id" in event ? "v2/events" : "v1/events/create",
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      // bigintReplacer keeps the bigint ns-epoch bounds full-precision on the wire.
      body: JSON.stringify(event, bigintReplacer),
      headers: robotoHeaders({
        resourceOwnerId: options?.resourceOwnerId,
        orgId,
      }),
    });
    return normalizeEventRecord(
      await response.json<EventRecord>(parseTimestampsAsBigInt),
    );
  }

  public async getEventById(
    eventId: string,
    options?: Partial<Options>,
  ): Promise<EventRecord> {
    const requestUrl = this.#httpClient.constructUrl(eventPath(eventId));
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return normalizeEventRecord(
      await response.json<EventRecord>(parseTimestampsAsBigInt),
    );
  }

  /**
   * Fetch every event relevant to a visualization workspace, described by its data
   * sources. Events anywhere within `datasetIds` and `fileIds` are returned
   * transitively (the entity itself and all of its files, topics, and message paths);
   * a loose file additionally surfaces its parent dataset's own events, which the
   * server resolves from the file id. This is the viz-specific `/v1/viz/events` query,
   * not a general-purpose event lookup. Follows `next_token` to accumulate every page,
   * and returns `[]` without a request when there are no data sources (the backend
   * rejects an empty request with a 400).
   *
   * `sessionIds` adds the events created on those sessions, read from
   * `/v1/sessions/id/<id>/events`. Pass them only when the org has session
   * events (`hasSessionEvents`): the route refuses every request in an org
   * without them, and that failure fails the whole call.
   */
  public async getWorkspaceEvents(
    sources: {
      datasetIds?: string[];
      fileIds?: string[];
      sessionIds?: string[];
    },
    options?: Partial<Options>,
  ): Promise<EventRecord[]> {
    const [datasetEvents, sessionEvents] = await Promise.all([
      this.#getDatasetWorkspaceEvents(
        sources.datasetIds ?? [],
        sources.fileIds ?? [],
        options,
      ),
      this.#getSessionEvents(sources.sessionIds ?? [], options),
    ]);
    return [...datasetEvents, ...sessionEvents];
  }

  /**
   * Fetch the events created on `sessionIds`: one paginated
   * `GET /v1/sessions/id/<id>/events` per session, at most
   * `MAX_CONCURRENT_SESSION_EVENT_LISTINGS` at a time, each following its own
   * `next_token`. Results are concatenated in `sessionIds` order; `[]` without
   * a request when there are none.
   */
  async #getSessionEvents(
    sessionIds: string[],
    options?: Partial<Options>,
  ): Promise<EventRecord[]> {
    const perSession: EventRecord[][] = sessionIds.map(() => []);
    let next = 0;
    const listNextSessions = async () => {
      while (next < sessionIds.length) {
        const index = next++;
        perSession[index] = await this.#getEventsForSession(
          sessionIds[index],
          options,
        );
      }
    };
    await Promise.all(
      Array.from(
        {
          length: Math.min(
            MAX_CONCURRENT_SESSION_EVENT_LISTINGS,
            sessionIds.length,
          ),
        },
        listNextSessions,
      ),
    );
    return perSession.flat();
  }

  async #getEventsForSession(
    sessionId: string,
    options?: Partial<Options>,
  ): Promise<EventRecord[]> {
    const items: EventRecord[] = [];
    let pageToken: string | undefined = undefined;
    do {
      const searchParams = new URLSearchParams();
      if (pageToken !== undefined) {
        searchParams.set("page_token", pageToken);
      }
      const requestUrl = this.#httpClient.constructUrl(
        `v1/sessions/id/${sessionId}/events`,
        searchParams,
      );
      const response = await this.#httpClient.get(requestUrl, {
        signal: options?.abortSignal,
        // The route authorizes against the session's own org, read from the
        // session record, and ignores this header; it is sent as on the other
        // org-scoped event reads.
        headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
      });
      const page = await response.json<PaginatedResponse<EventRecord>>(
        parseTimestampsAsBigInt,
      );
      items.push(...page.items.map(normalizeEventRecord));
      pageToken = page.next_token ?? undefined;
    } while (pageToken);
    return items;
  }

  async #getDatasetWorkspaceEvents(
    datasetIds: string[],
    fileIds: string[],
    options?: Partial<Options>,
  ): Promise<EventRecord[]> {
    if (datasetIds.length === 0 && fileIds.length === 0) {
      return [];
    }

    const requestUrl = this.#httpClient.constructUrl("v1/viz/events");
    const items: EventRecord[] = [];
    let pageToken: string | undefined = undefined;

    do {
      const body: WorkspaceEventsRequestBody = {
        dataset_ids: datasetIds,
        file_ids: fileIds,
        page_token: pageToken,
      };
      const response = await this.#httpClient.post(requestUrl, {
        signal: options?.abortSignal,
        body: JSON.stringify(body),
        headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
      });
      const page = await response.json<PaginatedResponse<EventRecord>>(
        parseTimestampsAsBigInt,
      );
      items.push(...page.items);
      pageToken = page.next_token ?? undefined;
    } while (pageToken);

    return items;
  }

  public async deleteEvent(
    eventId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(eventPath(eventId));
    const response = await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    await response.throwIfError();
  }

  /**
   * Delete events of every kind. Dataset events and session events are deleted
   * through their own endpoints, so a selection mixing both sends one request
   * per kind. The requests are independent: when one fails, the other kind's
   * events may already be gone.
   */
  public async deleteEvents(
    eventIds: string[],
    options?: Partial<Options>,
  ): Promise<void> {
    const sessionEventIds = eventIds.filter(isSessionEventId);
    const datasetEventIds = eventIds.filter(
      (eventId) => !isSessionEventId(eventId),
    );
    // Each delete route refuses more than MAX_EVENTS_PER_DELETE_BATCH IDs.
    const batches: [string, string[]][] = [
      ...chunk(datasetEventIds).map((ids): [string, string[]] => [
        "v1/events/delete",
        ids,
      ]),
      ...chunk(sessionEventIds).map((ids): [string, string[]] => [
        "v2/events/delete",
        ids,
      ]),
    ];
    await Promise.all(
      batches.map(async ([path, ids]) => {
        const body: DeleteEventsRequest = { event_ids: ids };
        const response = await this.#httpClient.post(
          this.#httpClient.constructUrl(path),
          {
            signal: options?.abortSignal,
            body: JSON.stringify(body),
            headers: robotoHeaders({
              resourceOwnerId: options?.resourceOwnerId,
            }),
          },
        );
        await response.throwIfError();
      }),
    );
  }

  public async updateEvent(
    eventId: string,
    updates: UpdateEventRequest,
    options?: Partial<Options>,
  ): Promise<EventRecord> {
    const requestUrl = this.#httpClient.constructUrl(eventPath(eventId));
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(updates),
    });
    return normalizeEventRecord(
      await response.json<EventRecord>(parseTimestampsAsBigInt),
    );
  }

  /**
   * One page of the org's event tags, from its dataset events and, with
   * `includeSessionEvents`, its session events, merged without repeats. The
   * page's `next_token` is set when either store has more; it only reports
   * truncation and cannot be passed back.
   */
  public async getTagsForOrg(
    params: { search?: string; limit?: number },
    options?: VocabularyOptions,
  ): Promise<PaginatedResponse<string>> {
    if (!options?.resourceOwnerId) {
      throw Error("getTagsForOrg requires an Org ID, none was provided");
    }
    const tagOptions = {
      resourceOwnerId: options.resourceOwnerId,
      abortSignal: options.abortSignal,
    };
    const noTags: PaginatedResponse<string> = {
      items: [],
      next_token: null,
      total_count: null,
    };

    const [datasetTags, sessionTags] = await Promise.all([
      getPaginatedTagsForOrg(
        this.#httpClient,
        "v1/events/tags",
        params,
        tagOptions,
      ),
      options.includeSessionEvents
        ? emptyIfUnauthorized(
            getPaginatedTagsForOrg(this.#httpClient, "v2/events/tags", params, {
              ...tagOptions,
              // The /v2/events routes scope by the caller's org, not the resource owner.
              orgId: options.resourceOwnerId,
            }),
            noTags,
          )
        : noTags,
    ]);
    // Each store answers with its first tags in order, so the union sorted is
    // the first page of the merged vocabulary.
    const merged = unique([...datasetTags.items, ...sessionTags.items]).sort();
    const items = merged.slice(0, params.limit);
    const truncated =
      items.length < merged.length ||
      datasetTags.next_token !== null ||
      sessionTags.next_token !== null;
    return {
      items,
      next_token: truncated
        ? (datasetTags.next_token ?? sessionTags.next_token ?? "")
        : null,
      // Unknown: summing the two stores' counts would count a shared tag twice.
      total_count: null,
    };
  }

  /**
   * The org's event metadata keys, from its dataset events and, with
   * `includeSessionEvents`, its session events, without repeats.
   */
  public async getMetadataKeysForOrg(
    options?: VocabularyOptions,
  ): Promise<string[]> {
    return this.getAllForOrg("metadata/keys", options);
  }

  /**
   * The org's event names, from its dataset events and, with
   * `includeSessionEvents`, its session events, without repeats.
   */
  public async getNamesForOrg(options?: VocabularyOptions): Promise<string[]> {
    return this.getAllForOrg("names", options);
  }

  private async getAllForOrg(
    resource: "metadata/keys" | "names",
    options?: VocabularyOptions,
  ): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error(
        `get${toPascalCase(resource)}ForOrg requires an Org ID, none was provided`,
      );
    }
    const [datasetValues, sessionValues] = await Promise.all([
      this.#getAll(`v1/events/${resource}`, options),
      options.includeSessionEvents
        ? emptyIfUnauthorized(
            this.#getAll(`v2/events/${resource}`, options),
            [],
          )
        : [],
    ]);
    return unique([...datasetValues, ...sessionValues]);
  }

  async #getAll(
    relativeUrl: string,
    options: Partial<Options>,
  ): Promise<string[]> {
    // No query params: neither the metadata-keys nor the names routes declare
    // any, so the router would drop them silently. Server-side search here is
    // a backend change first.
    const requestUrl = this.#httpClient.constructUrl(relativeUrl);
    const response = await this.#httpClient.get(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({
        resourceOwnerId: options.resourceOwnerId,
        // The /v2/events routes scope by the caller's org, not the resource owner.
        orgId: relativeUrl.startsWith("v2/")
          ? options.resourceOwnerId
          : undefined,
      }),
    });

    return await response.json<string[]>();
  }
}
